import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowsDownUpIcon, FunnelSimpleIcon, PlusIcon } from '@phosphor-icons/react'

import { Button, ButtonSelect } from '@reveni-io/reveni-ui'

import FiltersDrawer from '@/common/components/FiltersDrawer/FiltersDrawer'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import { showClearFiltersButton } from '@/common/components/SelectedFilters/helpers'
import SelectedFilters from '@/common/components/SelectedFilters/SelectedFilters'
import AppContext from '@/common/context/app.context'

import { filterNames } from '../constants'
import FiltersSkeleton from './FiltersSkeleton/FiltersSkeleton'
import { amountFilterConfig, amountFilterNames, filterTypes, matchModeFilterNames, sections } from './constants'
import {
  applyFilters,
  clearAmountFilter,
  dateFilterConfig,
  dateFilterNames,
  filterFields,
  getDefaultFilterValue,
  getEligibleFilterTypes,
  getFilterLabel,
  getInitialFilters,
  isFilterValid,
} from './helpers'
import * as Styles from './styles'

const Filters = ({
  loading,
  filters,
  onChangeFilter,
  query,
  onApplyFilters,
  orderBy,
  subscriptionPlan,
  returnReasons = [],
  rejectReasons = [],
  forReviewReasons = [],
  regions = [],
  onOpenNewRMADrawer,
  productCategories = [],
  productCollections = [],
  productTypes = [],
  isUnifiedDashboard,
  carriers = [],
  deliveryMethods = [],
  availableAlertTypes = [],
  fetchingAlertTypes,
  policyRules = [],
  readOnly,
  disabled,
}) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false)

  if (loading) {
    return <FiltersSkeleton />
  }

  const handleDeleteFilter = filterName => {
    if (dateFilterNames.includes(filterName)) {
      const config = dateFilterConfig[filterName]
      if (config.dateStart) onChangeFilter(config.dateStart, null, false)
      if (config.dateEnd) onChangeFilter(config.dateEnd, null, false)
      if (config.extra) onChangeFilter(config.extra, null, false)
      onChangeFilter(config.daysGte, null, false)
      onChangeFilter(config.daysLte, null, true)
    } else if (amountFilterNames.includes(filterName)) {
      clearAmountFilter(amountFilterConfig[filterName], onChangeFilter, true)
    } else if (matchModeFilterNames[filterName]) {
      onChangeFilter(matchModeFilterNames[filterName], null, false)
      onChangeFilter(filterName, null, true)
    } else {
      onChangeFilter(filterName, null, true)
    }
  }

  const handleClearFilters = () => {
    onChangeFilter(filterNames.status, [], false)
    onChangeFilter(filterNames.transactionType, [], false)
    onChangeFilter(filterNames.refundDateStart, null, false)
    onChangeFilter(filterNames.refundDateEnd, null, false)
    onChangeFilter(filterNames.refundDatesDaysGte, null, false)
    onChangeFilter(filterNames.refundDatesDaysLte, null, false)
    onChangeFilter(filterNames.deliveryMethodType, [], false)
    onChangeFilter(filterNames.deliveryMethod, null, false)
    onChangeFilter(filterNames.lastUpdate, null, false)
    onChangeFilter(filterNames.lastUpdatedGte, null, false)
    onChangeFilter(filterNames.lastUpdatedLte, null, false)
    onChangeFilter(filterNames.expiresIn, null, false)
    onChangeFilter(filterNames.refundMethod, [], false)
    onChangeFilter(filterNames.returnReason, [], false)
    onChangeFilter(filterNames.returnReasonType, [], false)
    onChangeFilter(filterNames.rejectReason, [], false)
    onChangeFilter(filterNames.forReviewReason, [], false)
    onChangeFilter(filterNames.region, [], false)
    onChangeFilter(filterNames.instant, null, false)
    onChangeFilter(filterNames.exchangeForOrderStatus, null, false)
    onChangeFilter(filterNames.paymentStatus, null, false)
    onChangeFilter(filterNames.refundStatus, null, false)
    onChangeFilter(filterNames.variantsSkus, [], false)
    onChangeFilter(filterNames.shippingLabelStatus, null, false)
    onChangeFilter(filterNames.exchangeCreatedGte, null, false)
    onChangeFilter(filterNames.exchangeCreatedLte, null, false)
    onChangeFilter(filterNames.exchangeCreatedDaysGte, null, false)
    onChangeFilter(filterNames.exchangeCreatedDaysLte, null, false)
    onChangeFilter(filterNames.hasAlerts, null, false)
    onChangeFilter(filterNames.pendingAlertTypes, [], false)
    onChangeFilter(filterNames.pendingAlertTypesMatch, null, false)
    onChangeFilter(filterNames.exchangedType, [], false)
    onChangeFilter(filterNames.refundCreatedGte, null, false)
    onChangeFilter(filterNames.refundCreatedLte, null, false)
    onChangeFilter(filterNames.refundCreatedDaysGte, null, false)
    onChangeFilter(filterNames.refundCreatedDaysLte, null, false)
    onChangeFilter(filterNames.productCategory, [], false)
    onChangeFilter(filterNames.productCollection, [], false)
    onChangeFilter(filterNames.productType, [], false)
    onChangeFilter(filterNames.isGift, null, false)
    onChangeFilter(filterNames.carrier, [], false)
    onChangeFilter(filterNames.orderType, null, false)
    onChangeFilter(filterNames.transactionTags, [], false)
    onChangeFilter(filterNames.transactionTagsMatch, null, false)
    onChangeFilter(filterNames.policyRules, [], false)
    onChangeFilter(filterNames.policyRulesMatch, null, false)
    onChangeFilter(filterNames.hasPolicyRules, null, false)

    for (const amountFilterName of amountFilterNames) {
      clearAmountFilter(amountFilterConfig[amountFilterName], onChangeFilter, false)
    }
  }

  const handleOpenDrawer = () => {
    setShowFiltersDrawer(true)
  }

  const handleCloseDrawer = () => {
    setShowFiltersDrawer(false)
  }

  const handleApplyDrawer = activeFilters => {
    applyFilters(activeFilters, onChangeFilter)
    onApplyFilters()
  }

  const hasActiveFilters = showClearFiltersButton(filters)
  const areFiltersReadOnly = readOnly || disabled
  const provider = isUnifiedDashboard ? null : info?.selectedStore?.provider
  const eligibleFilterTypes = getEligibleFilterTypes({
    filterTypes,
    subscriptionPlan,
    provider,
    hasAlertTypes: fetchingAlertTypes || availableAlertTypes.length > 0,
  })

  const fieldProps = {
    returnReasons,
    rejectReasons,
    forReviewReasons,
    regions,
    categories: productCategories,
    collections: productCollections,
    productTypes,
    carriers,
    deliveryMethods,
    availableAlertTypes,
    fetchingAlertTypes,
    policyRules,
    isUnifiedDashboard,
    provider,
    t,
  }

  const chipContext = {
    returnReasons,
    rejectReasons,
    forReviewReasons,
    regions,
    productCategories,
    productCollections,
    productTypes,
    carriers,
    deliveryMethods,
    policyRules,
  }

  return (
    <Styles.MainContainer>
      <Styles.HeaderContainer>
        <Styles.FiltersSection>
          <QueryFilter
            placeholder={t('Núm. de pedido, email o núm. de seguimiento')}
            defaultValue={query}
            onChange={query => onChangeFilter(filterNames.query, query)}
            onChangeValue={query => onChangeFilter(filterNames.query, query, false)}
            disabled={disabled}
          />
          <ButtonSelect
            Icon={ArrowsDownUpIcon}
            value={orderBy}
            buttonSize="s"
            buttonVariant="text"
            handleChange={value => onChangeFilter(filterNames.orderBy, value)}
            disabled={areFiltersReadOnly}
            menuItems={[
              { value: '-completed_at', label: t('Fecha de solicitud') },
              { value: '-last_updated_at', label: t('Fecha actualización') },
              { value: 'deadline', label: t('Días para validar') },
            ]}
          />
          <Button variant="text" size="s" onClick={handleOpenDrawer} disabled={areFiltersReadOnly}>
            <FunnelSimpleIcon size={24} weight="light" />
            {hasActiveFilters ? t('Editar filtros') : t('Añadir filtro')}
          </Button>
        </Styles.FiltersSection>
        {!isUnifiedDashboard && (
          <Button variant="outlined" onClick={onOpenNewRMADrawer}>
            <PlusIcon /> {t('Nuevo RMA')}
          </Button>
        )}
      </Styles.HeaderContainer>
      <SelectedFilters
        filters={filters}
        onDeleteFilter={handleDeleteFilter}
        onClickFilter={handleOpenDrawer}
        returnReasons={returnReasons}
        rejectReasons={rejectReasons}
        forReviewReasons={forReviewReasons}
        regions={regions}
        productCategories={productCategories}
        productCollections={productCollections}
        productTypes={productTypes}
        carriers={carriers}
        deliveryMethods={deliveryMethods}
        policyRules={policyRules}
        readOnly={areFiltersReadOnly}
      />

      <FiltersDrawer
        show={showFiltersDrawer}
        onClose={handleCloseDrawer}
        onApply={handleApplyDrawer}
        onClearFilters={handleClearFilters}
        appliedFilters={filters}
        filterTypes={filterTypes}
        eligibleFilterTypes={eligibleFilterTypes}
        sections={sections}
        getInitialFilters={getInitialFilters}
        getDefaultFilterValue={getDefaultFilterValue}
        getFilterLabel={getFilterLabel}
        isFilterValid={isFilterValid}
        filterFields={filterFields}
        fieldProps={fieldProps}
        chipContext={chipContext}
      />
    </Styles.MainContainer>
  )
}

export default Filters
